import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Lock, Shield, ArrowRight, Activity, Search, Banknote, BadgeCheck, Fingerprint, Scale, FileSearch, KeyRound } from 'lucide-react';
import { LOGO_URL } from '../utils/brand';
import RequestAccessModal from '../components/RequestAccessModal';
// eslint-disable-next-line
const APP_VERSION = require('../../package.json').version;

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.toLowerCase().trim(), password, rememberMe);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" data-testid="login-page"
      style={{ background: 'radial-gradient(ellipse at top, #0f2640 0%, #0a1830 45%, #050f1d 100%)' }}>

      <style>{`
        @keyframes float1 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(30px,-40px) scale(1.1)} }
        @keyframes float2 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-40px,30px) scale(1.15)} }
        @keyframes float3 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(20px,50px) scale(1.05)} }
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes pulse-ring { 0%{box-shadow:0 0 0 0 rgba(14,165,233,0.4)} 70%{box-shadow:0 0 0 12px rgba(14,165,233,0)} 100%{box-shadow:0 0 0 0 rgba(14,165,233,0)} }
        @keyframes drift-slow { 0%,100%{transform:translate(0,0) rotate(0deg)} 33%{transform:translate(15px,-20px) rotate(4deg)} 66%{transform:translate(-10px,15px) rotate(-3deg)} }
        @keyframes drift-tilt { 0%,100%{transform:translate(0,0) rotate(-12deg)} 50%{transform:translate(-12px,18px) rotate(-6deg)} }
        @keyframes spin-slow { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      `}</style>

      {/* Animated background orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full opacity-[0.18]"
          style={{ background: 'radial-gradient(circle, #0ea5e9, transparent 70%)', animation: 'float1 8s ease-in-out infinite' }} />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full opacity-[0.14]"
          style={{ background: 'radial-gradient(circle, #6366f1, transparent 70%)', animation: 'float2 10s ease-in-out infinite' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-[0.10]"
          style={{ background: 'radial-gradient(circle, #06b6d4, transparent 70%)', animation: 'float3 12s ease-in-out infinite' }} />
      </div>

      {/* Thematic investigator icons drifting in the corners */}
      <div className="absolute inset-0 pointer-events-none hidden md:block" aria-hidden="true">
        {/* Magnifying glass — top left, slowly drifting */}
        <Search className="absolute top-[8%] left-[5%] w-56 h-56 text-sky-400/[0.18]"
          style={{ animation: 'drift-slow 14s ease-in-out infinite', strokeWidth: 1.5, filter: 'drop-shadow(0 0 24px rgba(56,189,248,0.35))' }} />

        {/* Wad of cash — bottom left, tilted */}
        <Banknote className="absolute bottom-[8%] left-[7%] w-64 h-64 text-emerald-400/[0.18]"
          style={{ animation: 'drift-tilt 16s ease-in-out infinite', strokeWidth: 1.5, transform: 'rotate(-12deg)', filter: 'drop-shadow(0 0 24px rgba(52,211,153,0.35))' }} />

        {/* Investigator badge — top right */}
        <BadgeCheck className="absolute top-[10%] right-[5%] w-60 h-60 text-amber-300/[0.18]"
          style={{ animation: 'drift-slow 18s ease-in-out infinite 2s', strokeWidth: 1.5, filter: 'drop-shadow(0 0 24px rgba(252,211,77,0.35))' }} />

        {/* Fingerprint — bottom right */}
        <Fingerprint className="absolute bottom-[7%] right-[5%] w-56 h-56 text-indigo-300/[0.18]"
          style={{ animation: 'drift-tilt 20s ease-in-out infinite 1s', strokeWidth: 1.5, filter: 'drop-shadow(0 0 24px rgba(165,180,252,0.35))' }} />

        {/* Scale of justice — center left */}
        <Scale className="absolute top-[48%] left-[2%] w-40 h-40 text-violet-300/[0.14]"
          style={{ animation: 'drift-slow 22s ease-in-out infinite 3s', strokeWidth: 1.5, filter: 'drop-shadow(0 0 20px rgba(196,181,253,0.3))' }} />

        {/* Audit file — center right */}
        <FileSearch className="absolute top-[42%] right-[2%] w-40 h-40 text-cyan-300/[0.16]"
          style={{ animation: 'drift-tilt 24s ease-in-out infinite 4s', strokeWidth: 1.5, filter: 'drop-shadow(0 0 20px rgba(103,232,249,0.3))' }} />
      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className="w-full max-w-lg px-6 relative z-10">
        {/* Glow behind card */}
        <div className="absolute -inset-4 rounded-3xl opacity-20 blur-2xl"
          style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 50%, #0ea5e9 100%)' }} />

        <div className="relative rounded-2xl border border-white/[0.08] p-8 backdrop-blur-xl"
          style={{ background: 'linear-gradient(145deg, rgba(15,32,53,0.95) 0%, rgba(10,22,40,0.98) 100%)', boxShadow: '0 0 80px -20px rgba(14,165,233,0.15), 0 25px 50px -12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)' }}>

          {/* Top gradient accent line */}
          <div className="absolute top-0 left-8 right-8 h-[2px] rounded-full"
            style={{ background: 'linear-gradient(90deg, transparent, #0ea5e9, #6366f1, #0ea5e9, transparent)' }} />

          {/* Header */}
          <div className="text-center mb-8">
            <img
              src={LOGO_URL}
              alt="FEC Watchdog"
              className="mx-auto w-full max-w-[420px] sm:max-w-[460px] h-auto object-contain drop-shadow-[0_0_28px_rgba(14,165,233,0.4)] select-none"
              draggable={false}
              data-testid="login-logo"
            />
            <p className="text-slate-300 text-sm mt-3 tracking-wide">Campaign Finance Intelligence</p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-5 px-4 py-3 rounded-xl text-red-300 text-sm border border-red-500/20" data-testid="login-error"
              style={{ background: 'linear-gradient(135deg, rgba(127,29,29,0.2), rgba(153,27,27,0.1))' }}>
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-slate-200 uppercase tracking-wider mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-xl text-white placeholder-slate-400 focus:outline-none transition-all duration-200"
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.25)' }}
                onFocus={e => { e.target.style.border = '1px solid rgba(56,189,248,0.7)'; e.target.style.boxShadow = 'inset 0 1px 4px rgba(0,0,0,0.2), 0 0 0 3px rgba(14,165,233,0.15)'; }}
                onBlur={e => { e.target.style.border = '1px solid rgba(255,255,255,0.15)'; e.target.style.boxShadow = 'inset 0 1px 4px rgba(0,0,0,0.25)'; }}
                data-testid="login-email-input"
                required
                autoFocus
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-200 uppercase tracking-wider mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 pr-12 rounded-xl text-white placeholder-slate-400 focus:outline-none transition-all duration-200"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.25)' }}
                  onFocus={e => { e.target.style.border = '1px solid rgba(56,189,248,0.7)'; e.target.style.boxShadow = 'inset 0 1px 4px rgba(0,0,0,0.2), 0 0 0 3px rgba(14,165,233,0.15)'; }}
                  onBlur={e => { e.target.style.border = '1px solid rgba(255,255,255,0.15)'; e.target.style.boxShadow = 'inset 0 1px 4px rgba(0,0,0,0.25)'; }}
                  data-testid="login-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-sky-400 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center">
              <label className="flex items-center gap-2.5 cursor-pointer select-none group" data-testid="remember-me-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded accent-sky-500"
                  data-testid="remember-me-checkbox"
                />
                <span className="text-sm text-slate-300 group-hover:text-white transition-colors">Remember me</span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 text-white font-semibold rounded-xl transition-all duration-300 relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #0ea5e9, #6366f1)' }}
              data-testid="login-submit-btn"
            >
              <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: 'linear-gradient(135deg, #38bdf8, #818cf8)' }} />
              <span className="relative flex items-center justify-center gap-2">
                {loading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing In...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </>
                )}
              </span>
            </button>
          </form>

          {/* Divider with a subtle "or" label so the eye reaches the
              secondary CTA below. */}
          <div className="mt-7 mb-5 flex items-center gap-3">
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.14), transparent)' }} />
            <span className="text-[11px] uppercase tracking-[0.2em] text-slate-400">or</span>
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.14), transparent)' }} />
          </div>

          {/* Footer */}
          <div className="flex flex-col items-center gap-4">
            <button
              onClick={() => navigate('/learn-more')}
              className="group w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 border-sky-400/50 bg-sky-500/5 hover:bg-sky-500/15 hover:border-sky-400 hover:shadow-[0_0_24px_-4px_rgba(56,189,248,0.55)] text-sky-200 hover:text-white text-sm font-semibold tracking-wide transition-all duration-200"
              data-testid="learn-more-btn"
            >
              <FileSearch className="w-4 h-4" />
              <span>Explore the App &mdash; Learn More</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => setRequestOpen(true)}
              className="group w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border border-slate-600/60 bg-slate-800/40 hover:bg-slate-700/50 hover:border-slate-400 text-slate-200 hover:text-white text-sm font-medium tracking-wide transition-all duration-200"
              data-testid="request-access-btn"
            >
              <KeyRound className="w-4 h-4 text-amber-300" />
              <span>Request Access</span>
            </button>
            <div className="flex items-center justify-center gap-5 text-[11px] text-slate-300">
              <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-emerald-400" /> SSL Secure</span>
              <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-sky-400" /> 256-bit Encryption</span>
              <button
                onClick={() => navigate('/privacy')}
                className="text-slate-300 hover:text-white hover:underline underline-offset-2 transition-colors"
                data-testid="login-privacy-link"
              >
                Privacy
              </button>
            </div>
            <p className="text-[11px] text-slate-400">&copy; 2026 FEC Watchdog &mdash; Created by Bill Casso <span className="opacity-70">&middot; v{APP_VERSION}</span></p>
          </div>
        </div>
      </div>
      <RequestAccessModal open={requestOpen} onClose={() => setRequestOpen(false)} />
    </div>
  );
}
